import React, { useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import ViewListIcon from "@material-ui/icons/ViewList";
import ViewModuleIcon from "@material-ui/icons/ViewModule";

const useStyles = makeStyles((theme) => ({
  toggle: {
    display: "inline-flex",
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
    borderRadius: 10,
    overflow: "hidden",
    flexShrink: 0,
  },
  btn: {
    width: 36,
    height: 36,
    padding: 0,
    borderRadius: 0,
    color: theme.palette.text.secondary,
    "&.active": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
    },
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
    padding: 12,
    width: "100%",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: 8,
      padding: 0,
    },
  },
  gridDense: {
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: 8,
    padding: 8,
  },
  card: {
    padding: 14,
    borderRadius: 14,
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
    backgroundColor: theme.palette.background.paper,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    [theme.breakpoints.down("sm")]: {
      padding: 10,
      borderRadius: 16,
      gap: 6,
    },
  },
  title: {
    fontWeight: 800,
    color: theme.palette.text.primary,
    wordBreak: "break-word",
  },
  meta: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    wordBreak: "break-word",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "auto",
    gap: 8,
    flexWrap: "wrap",
  },
}));

export const useViewMode = (storageKey, defaultValue = "table") => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const key = `watoolx.viewMode.${storageKey}`;
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) return saved;
    } catch {
      /* ignore */
    }
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 960px)").matches) {
      return "cards";
    }
    return defaultValue;
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, viewMode);
    } catch {
      /* ignore quota */
    }
  }, [key, viewMode]);

  return [isMobile ? "cards" : viewMode, setViewMode];
};

export const ViewModeToggle = ({ value, onChange }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  if (isMobile) return null;
  return (
    <div className={classes.toggle}>
      <Tooltip title="Tabla">
        <IconButton
          className={`${classes.btn} ${value === "table" ? "active" : ""}`}
          onClick={() => onChange("table")}
        >
          <ViewListIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Tarjetas">
        <IconButton
          className={`${classes.btn} ${value === "cards" ? "active" : ""}`}
          onClick={() => onChange("cards")}
        >
          <ViewModuleIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export const RecordsCardsGrid = ({ children, dense }) => {
  const classes = useStyles();
  return <div className={`${classes.grid} ${dense ? classes.gridDense : ""}`}>{children}</div>;
};

export const RecordCard = ({ title, children, actions, onClick, className }) => {
  const classes = useStyles();
  return (
    <div
      className={`${classes.card} ${className || ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={onClick ? { cursor: "pointer" } : undefined}
    >
      {title ? <div className={classes.title}>{title}</div> : null}
      {children}
      {actions ? (
        <div className={classes.footer} onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      ) : null}
    </div>
  );
};

export const RecordCardMeta = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.meta}>{children}</div>;
};

export default ViewModeToggle;
